/**
 * O que aparece no menu desta escola.
 *
 * **O número que motiva a tela.** O menu de cadastro do URÂNIA tem oito
 * entradas; o nosso tem dezenove. A instalação que medimos usa quatorze — e
 * ensino superior não tem hora-atividade nem jornada CLT, a ESPM está num
 * campus só, não agrupa disciplinas em áreas, e o cadastro dela diz
 * *"não existem trilhas de aprofundamento"*.
 *
 * O concorrente mostra tudo a todos. Uma escola que declara o que usa e vê o
 * menu encolher é vantagem sobre ele, não paridade — e o custo de adoção que
 * ela relatou é *"uma semana de cadastro, e não terminamos"*.
 *
 * **A promessa que a tela precisa fazer, e cumprir.** Desmarcar não apaga
 * nada: o dado continua no banco, as telas continuam alcançáveis, e o motor
 * continua recebendo tudo. É só a entrada do menu que some. Está escrito aqui
 * em palavras porque é a dúvida que faz alguém não mexer — e há teste de
 * servidor prendendo cada uma das três partes.
 */

import { useEffect, useMemo, useState } from 'react'

import { ErroApi, api, type Grupo, type Recurso } from '../api'
import { Icon } from '../components/Icon'

const ROTULO_GRUPO: Record<Grupo, string> = {
  ESTRUTURA: 'Estrutura',
  ACADEMICO: 'Dados',
  AGENDAVEL: 'Grade curricular',
}

export function AjustarMenu({
  projeto,
  aoMudar,
}: {
  projeto: string
  /** Avisa o App para remontar a navegação sem recarregar a página. */
  aoMudar: () => void
}) {
  const [recursos, setRecursos] = useState<Recurso[]>([])
  const [ativos, setAtivos] = useState<Set<string>>(new Set())
  const [erro, setErro] = useState<string | null>(null)
  const [salvo, setSalvo] = useState(false)
  const [ocupado, setOcupado] = useState(false)

  useEffect(() => {
    api
      .recursosDoProjeto(projeto)
      .then((itens) => {
        setRecursos(itens)
        setAtivos(new Set(itens.filter((r) => r.ativo !== false).map((r) => r.caminho)))
      })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'não foi possível carregar'))
  }, [projeto])

  const porGrupo = useMemo(() => {
    const mapa = new Map<Grupo, Recurso[]>()
    for (const r of recursos) {
      const lista = mapa.get(r.grupo) ?? []
      lista.push(r)
      mapa.set(r.grupo, lista)
    }
    return [...mapa]
  }, [recursos])

  async function gravar() {
    setOcupado(true)
    setErro(null)
    try {
      await api.definirMenu(projeto, [...ativos])
      setSalvo(true)
      aoMudar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível gravar')
    } finally {
      setOcupado(false)
    }
  }

  const ocultos = recursos.length - ativos.size

  return (
    <>
      <header className="page-head">
        <h1>O que aparece no menu</h1>
        <p className="page-head__note">
          Marque o que esta escola usa. {ocultos === 0 ? 'Nada está oculto.' : null}
        </p>
      </header>

      <p className="callout">
        <strong>Desmarcar não apaga nada.</strong>
        <span>
          {' '}
          O cadastro continua no banco, a tela continua alcançável e a geração da grade continua
          levando tudo em conta. Some só a entrada do menu, e marcar de novo traz exatamente o que
          estava lá.
        </span>
      </p>

      {erro && (
        <p className="state state--pendencia" role="alert">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}

      {porGrupo.map(([grupo, itens]) => (
        <section className="section" key={grupo}>
          <div className="section__head">
            <h2>{ROTULO_GRUPO[grupo] ?? grupo}</h2>
            <span className="field__hint">
              {itens.filter((r) => ativos.has(r.caminho)).length} de {itens.length}
            </span>
          </div>

          <ul className="stack">
            {itens.map((r) => (
              <li key={r.caminho} className="row">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={ativos.has(r.caminho)}
                    onChange={(e) => {
                      const proximo = new Set(ativos)
                      if (e.target.checked) proximo.add(r.caminho)
                      else proximo.delete(r.caminho)
                      setAtivos(proximo)
                      setSalvo(false)
                    }}
                  />
                  {r.rotulo}
                </label>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <div className="solta__acoes no-print">
        <button
          type="button"
          className="btn btn--primary"
          disabled={ocupado}
          onClick={() => void gravar()}
        >
          Gravar
        </button>
        <span className="field__hint">
          {ativos.size} de {recursos.length} seções no menu
        </span>
        {salvo && (
          <span className="state state--ok">
            <Icon name="ok" size={14} />
            Gravado.
          </span>
        )}
      </div>
    </>
  )
}
